import { useState, useEffect, useRef } from "react";
import {
  Braces,
  Input,
  SelectInput,
  SquarePlus,
  useToast,
  Spinner,
  SquareFunction,
} from "@repo/ui";
import { useCustomers } from "~/pages/dashboard/task-manager/use-customers";
import { useWorkflowCreationContext } from "../../../workflow-creation-context-provider";
import { WorkflowsDialogFooter } from "../workflows-dialog-footer";
import { useCreateWorkflow } from "./use-create-workflow";
import { ViewNavigationButton } from "./view-navigation-button";

type Props = {
  onClose?: () => void;
};

type ValidationField = "name" | "customerId";

export const DefaultView = ({ onClose }: Props) => {
  const { showToast } = useToast();
  const {
    state: { workflow },
    updateState,
  } = useWorkflowCreationContext();

  const { customerId, name } = workflow || {
    name: "",
    customerId: null,
  };
  const { customers, isLoading: isLoadingCustomers } = useCustomers();
  const { handleMutate, isPending } = useCreateWorkflow();
  const [errors, setErrors] = useState<Partial<Record<ValidationField, string>>>({});
  const hasShownNoCustomersToast = useRef(false);

  const handleOnSave = async () => {
    const newErrors: Partial<Record<ValidationField, string>> = {};
    let hasError = false;

    if (!name?.trim()) {
      newErrors.name = "Workflow Name is required.";
      hasError = true;
    }
    if (customers && customers.length > 0 && !customerId) {
      newErrors.customerId = "Customer is required.";
      hasError = true;
    }
    setErrors(newErrors);
    if (hasError) {
      return;
    }
    await handleMutate();
  };

  const clearError = (field: ValidationField) => {
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  useEffect(() => {
    if (!isLoadingCustomers && customers?.length === 0 && !hasShownNoCustomersToast.current) {
      hasShownNoCustomersToast.current = true;
      showToast(
        "error",
        "No customers found. Please create a customer before creating a workflow.",
        "No Customers"
      );
    }
  }, [isLoadingCustomers, customers, showToast]);

  if (isLoadingCustomers) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (customers?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
        <p className="text-gray-600">
          No customers found. Please create a customer before creating a workflow.
        </p>
        <WorkflowsDialogFooter onCancel={onClose} disableSave />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10">
      <Input
        label="Workflow Name"
        error={errors.name}
        required
        placeholder="Workflow Name"
        value={name}
        onChange={(e) => {
          clearError("name");
          updateState("workflow", { ...workflow, name: e.target.value });
        }}
      />
      {!!customers?.length && (
        <SelectInput
          error={errors.customerId}
          label="Customer"
          options={customers.map((customer) => ({
            label: customer.name,
            value: String(customer.id),
          }))}
          placeholder="Select Customer"
          value={String(customerId)}
          onChange={(val) => {
            clearError("customerId");
            updateState("workflow", { ...workflow, customerId: Number(val) });
          }}
          required
        />
      )}
      <div className="flex flex-col gap-7">
        <ViewNavigationButton
          id="metadata-field-button"
          icon={<Braces strokeWidth="1px" className="text-blue-600" />}
          label="Create Metadata Fields"
          onClick={() => updateState("currentView", "fields")}
        />
        <ViewNavigationButton
          id="item-status-button"
          icon={<SquarePlus strokeWidth="1.5px" className="text-blue-600" size="16px" />}
          label="Create Item Status"
          onClick={() => updateState("currentView", "statuses")}
        />
        <ViewNavigationButton
          id="item-action-button"
          icon={<SquareFunction strokeWidth="1.5px" className="text-blue-600" size="16px" />}
          label="Create Item Action"
          onClick={() => updateState("currentView", "actions")}
        />
      </div>
      <WorkflowsDialogFooter loading={isPending} onSave={handleOnSave} onCancel={onClose} />
    </div>
  );
};
